import { SxProps, Theme } from '@mui/material';
import { DESCRIPTION_GRAY, TEXT_DARK } from '../../../styles/GlobalStyles';
import { getMaskedIconStyle } from '../../../services/Utilities';

/**
 * Styles for Standards component
 * Migrated from Angular component styles
 */

const STANDARD_TAG_HEIGHT = '28px';

export const standardsClassNames = {
  chevronIcon: 'standards-chevron-icon',
};

export const standardsStyles = {
  container: (theme: Theme) =>
    ({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(0.5),
      width: '100%',
      minWidth: 0,
      position: 'relative',
    }) as SxProps<Theme>,

  controlsRow: (theme: Theme) =>
    ({
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      width: '100%',
      minWidth: 0,
    }) as SxProps<Theme>,

  standardSetLabel: (theme: Theme, expanded: boolean) =>
    ({
      flexShrink: 0,
      fontSize: theme.typography.caption.fontSize,
      height: STANDARD_TAG_HEIGHT,
      boxSizing: 'border-box',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(0, 1),
      appearance: 'none',
      fontFamily: 'inherit',
      borderRadius: '3px',
      cursor: 'pointer',
      border: expanded ? `1px solid ${theme.palette.primary.main}` : '1px solid #929292',
      backgroundColor: expanded ? theme.palette.primary.main : 'transparent',
      color: expanded ? theme.palette.primary.contrastText : theme.palette.text.primary,
      '&:hover': {
        border: expanded ? `1px solid ${theme.palette.primary.main}` : '1px solid #F7F7F7',
        backgroundColor: expanded ? theme.palette.primary.dark : '#F7F7F7',
        color: expanded ? theme.palette.primary.contrastText : '#929292',
      },
    }) as SxProps<Theme>,

  expandButton: (theme: Theme) =>
    ({
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      padding: 0,
      border: 0,
      appearance: 'none',
      background: 'transparent',
      fontFamily: 'inherit',
      fontSize: '20px',
      lineHeight: 1,
      color: TEXT_DARK,
      '&:hover': {
        color: TEXT_DARK,
      },
    }) as SxProps<Theme>,

  scrollButton: (theme: Theme) =>
    ({
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
      padding: theme.spacing(0, 0.5),
      border: 0,
      appearance: 'none',
      background: 'transparent',
      fontFamily: 'inherit',
      fontSize: '20px',
      lineHeight: 1,
      color: TEXT_DARK,
      '&:hover': {
        color: TEXT_DARK,
      },
    }) as SxProps<Theme>,

  chevronIcon: () =>
    ({
      display: 'block',
      width: '20px',
      height: '20px',
      flex: '0 0 auto',
      backgroundColor: 'currentColor',
      mask: 'var(--standards-icon-url) center / contain no-repeat',
      WebkitMask: 'var(--standards-icon-url) center / contain no-repeat',
    }) as SxProps<Theme>,

  expandedStandardsRow: (theme: Theme) =>
    ({
      flex: '1 1 auto',
      display: 'flex',
      flexWrap: 'nowrap',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      minWidth: 0,
      maxWidth: '100%',
      overflowX: 'auto',
      overflowY: 'hidden',
    }) as SxProps<Theme>,

  standardButton: (theme: Theme, selected: boolean, readonly: boolean) =>
    ({
      fontSize: theme.typography.caption.fontSize,
      height: STANDARD_TAG_HEIGHT,
      boxSizing: 'border-box',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      padding: theme.spacing(0, 1),
      appearance: 'none',
      fontFamily: 'inherit',
      borderRadius: '3px',
      cursor: readonly ? 'default' : 'pointer',
      border: `1px solid ${selected ? theme.palette.primary.main : '#929292'}`,
      backgroundColor: readonly ? '#929292' : 'transparent',
      color: readonly ? '#FFFFFF' : (selected ? theme.palette.primary.main : theme.palette.text.primary),
      '&:hover': readonly
        ? {}
        : {
            border: selected ? `1px solid ${theme.palette.primary.main}` : '1px solid #F7F7F7',
            backgroundColor: selected ? 'transparent' : '#F7F7F7',
            color: selected ? theme.palette.primary.main : '#929292',
          },
    }) as SxProps<Theme>,

  expectationContainer: (theme: Theme) =>
    ({
      position: 'absolute',
      top: `calc(100% + ${theme.spacing(1)})`,
      left: 0,
      right: 0,
      width: '100%',
      paddingRight: theme.spacing(0.5),
      boxSizing: 'border-box',
      zIndex: 1,
    }) as SxProps<Theme>,

  expectationLabel: (theme: Theme) =>
    ({
      fontSize: theme.typography.caption.fontSize,
      color: DESCRIPTION_GRAY,
      paddingTop: theme.spacing(0.5),
    }) as SxProps<Theme>,

  breakoutItem: (theme: Theme) =>
    ({
      fontSize: theme.typography.caption.fontSize,
      color: DESCRIPTION_GRAY,
      paddingTop: theme.spacing(0.5),
      '& strong': {
        fontWeight: theme.typography.fontWeightBold,
      },
    }) as SxProps<Theme>,

  breakoutText: (theme: Theme) =>
    ({
      marginLeft: theme.spacing(0.75),
    }) as SxProps<Theme>,
};

export function getStandardsIconStyle(src: string) {
  return getMaskedIconStyle('--standards-icon-url', src);
}

export default standardsStyles;
